/**
 * SinceYouLastOpenedCard — ADI Operational Continuity Sprint
 * Phase 1 (Feb 28, 2026).
 *
 * Calm orientation cue at the top of PupilProfile. Shows up to 3
 * meaningful changes that landed since the instructor's last view
 * of THIS pupil's profile.
 *
 * Doctrine pins baked in:
 *
 *   • Instructor orientation, NOT an activity feed.
 *     The instructor should feel "I'm back in context", not
 *     "Here is another admin wall."
 *
 *   • Max 3 items. The backend caps the array; the frontend
 *     also slices defensively.
 *
 *   • Silent rendering when there are no items (including the
 *     first-ever view of this profile — the backend writes the
 *     marker without computing a delta on first visit, so the
 *     card returns null).
 *
 *   • Reads from ONE endpoint
 *     (`GET /api/instructor/pupil-profile-delta/{pupilId}`).
 *     The endpoint atomically writes the new "last viewed" marker
 *     after the delta is computed, so this component does not
 *     need a separate update call.
 *
 *   • No counters, no scoreboards, no risk ratings, no streaks,
 *     no badges, no AI verbs, no notification-centre framing.
 *
 *   • No pupil-side surface. The card never appears in any
 *     learner route — it lives only on /pupils/:pupilId.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { History } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_DELTA_ITEMS = 3;

export default function SinceYouLastOpenedCard({ pupilId }) {
  const [items, setItems] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!pupilId) return undefined;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/instructor/pupil-profile-delta/${pupilId}`,
          { withCredentials: true },
        );
        if (cancelled) return;
        const list = Array.isArray(data?.items) ? data.items : [];
        setItems(list.slice(0, MAX_DELTA_ITEMS));
      } catch {
        // Silent — orientation cue is best-effort. Profile is
        // operationally truthful without it.
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [pupilId]);

  // Calm doctrine: silent before fetch resolves AND when there
  // are no meaningful changes.
  if (!ready) return null;
  if (!items.length) return null;

  return (
    <section
      data-testid="pupil-profile-since-last-card"
      aria-labelledby="psloc-title"
      className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4 shadow-sm"
    >
      <div className="flex items-center gap-2 mb-2">
        <History
          className="w-3.5 h-3.5 text-slate-400"
          aria-hidden="true"
        />
        <p
          id="psloc-title"
          className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-slate-400"
        >
          Since you last opened this profile
        </p>
      </div>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li
            key={`${it.kind}:${i}`}
            data-testid={`pupil-profile-since-last-item-${it.kind}`}
            data-kind={it.kind}
            className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed"
          >
            {it.message}
          </li>
        ))}
      </ul>
    </section>
  );
}
